/**
 * Cross-domain authentication utilities for sharing auth status between
 * subdomains using parent domain cookies
 */

import cognitoConfig from '../config/infrastructure_outputs.json';

const AUTH_COOKIE_NAME = 'kolega_auth_status';
const COOKIE_MAX_AGE = 24 * 60 * 60; // 24 hours in seconds

/**
 * Get the cookie domain by removing the first subdomain from cognitoConfig domain
 */
const getCookieDomain = (): string => {
  const customDomainUrl = cognitoConfig.CustomDomainURL;
  if (!customDomainUrl) {
    throw new Error('CustomDomainURL not found in cognitoConfig');
  }
  
  // Remove protocol and get hostname
  const hostname = customDomainUrl.replace(/^https?:\/\//, '');
  
  // Handle localhost
  if (hostname === 'localhost' || hostname.includes('localhost')) {
    return 'localhost';
  }
  
  // Remove first subdomain: studio.app.kolega.co.uk → app.kolega.co.uk
  const parts = hostname.split('.');
  if (parts.length <= 1) {
    return `.${hostname}`;
  }
  
  const domainWithoutFirstSubdomain = parts.slice(1).join('.');
  return `.${domainWithoutFirstSubdomain}`;
};

export interface SetAuthCookieParams {
  isAuthenticated: boolean;
  userId: string;
  email: string;
  displayName: string | null;
}

/**
 * Set authentication status cookie on parent domain
 * This allows all subdomains of the parent domain to access the cookie
 */
export const setAuthCookie = (authData: SetAuthCookieParams): void => {
  try {
    const cookieValue = JSON.stringify({
      isAuthenticated: authData.isAuthenticated,
      userId: authData.userId,
      email: authData.email,
      displayName: authData.displayName || null,
      timestamp: Date.now() // For debugging/expiry checking
    });

    // Encode the JSON to handle special characters
    const encodedValue = encodeURIComponent(cookieValue);
    
    // Set cookie on parent domain with secure flags
    const cookieDomain = getCookieDomain();
    const isSecure = typeof window !== 'undefined' && 
      (window.location.protocol === 'https:' || cookieDomain === 'localhost');
    
    const cookieParts = [
      `${AUTH_COOKIE_NAME}=${encodedValue}`,
      `Domain=${cookieDomain}`,
      `Max-Age=${COOKIE_MAX_AGE}`,
      'Path=/',
      'SameSite=Lax' // Allow cross-site access within same domain
    ];
    
    // Add Secure flag only for HTTPS (not localhost)
    if (isSecure && cookieDomain !== 'localhost') {
      cookieParts.push('Secure');
    }
    
    const cookieString = cookieParts.join('; ');
    
    document.cookie = cookieString;
    
    console.log('Cross-domain auth cookie set successfully:', {
      domain: cookieDomain,
      userId: authData.userId,
      email: authData.email
    });
  } catch (error) {
    console.error('Error setting cross-domain auth cookie:', error);
  }
};

/**
 * Clear authentication cookie from parent domain
 * This will remove the authentication status from both domains
 */
export const clearAuthCookie = (): void => {
  try {
    // Set cookie with past expiry date to delete it
    const cookieDomain = getCookieDomain();
    const isSecure = typeof window !== 'undefined' && 
      (window.location.protocol === 'https:' || cookieDomain === 'localhost');
    
    const cookieParts = [
      `${AUTH_COOKIE_NAME}=`,
      `Domain=${cookieDomain}`,
      'Max-Age=0',
      'Path=/',
      'SameSite=Lax',
      'expires=Thu, 01 Jan 1970 00:00:00 GMT'
    ];
    
    // Add Secure flag only for HTTPS (not localhost) for consistency
    if (isSecure && cookieDomain !== 'localhost') {
      cookieParts.push('Secure');
    }
    
    const cookieString = cookieParts.join('; ');
    
    document.cookie = cookieString;
    
    console.log('Cross-domain auth cookie cleared successfully for domain:', cookieDomain);
  } catch (error) {
    console.error('Error clearing cross-domain auth cookie:', error);
  }
};
